import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';

const ROLE_LABELS: Record<string, string> = {
  USER: 'Usuário',
  COORDINATOR: 'Coordenador',
  SYSTEM_ADMIN: 'Admin Sistema',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join('');
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const { isCollapsed } = useSidebar();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on click outside
  useEffect(() => {
    if (!open) {
      return;
    }
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  if (!user) {
    return null;
  }

  const initials = getInitials(user.name);
  const roleLabel = ROLE_LABELS[user.role] || user.role;

  return (
    <div ref={containerRef} className="relative">
      {/* Avatar trigger */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-3 w-full rounded-xl p-2 hover:bg-secondary-100 transition-colors"
      >
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200"
          style={{ width: 38, height: 38 }}
        >
          <span className="text-sm font-semibold text-primary-700">{initials}</span>
        </div>
        {!isCollapsed && (
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium text-secondary-900 truncate">{user.name}</p>
            <p className="text-xs text-secondary-500 truncate">{roleLabel}</p>
          </div>
        )}
      </button>

      {/* Popover menu */}
      {open && (
        <div
          className={`absolute z-50 w-56 rounded-xl bg-white border border-secondary-200 shadow-lg py-2 ${
            isCollapsed ? 'left-full ml-2 bottom-0' : 'bottom-full mb-2 left-0'
          }`}
        >
          {/* User info header */}
          <div className="px-4 py-2 border-b border-secondary-100">
            <p className="text-sm font-medium text-secondary-900 truncate">{user.name}</p>
            <p className="text-xs text-secondary-500">{roleLabel}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/change-password');
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
            >
              <KeyRound className="w-4 h-4" />
              <span>Alterar Senha</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
